class ProblemScaleCard {
	static selectors = {
		block: '[data-problem-scale-card]',
		dynamicText: '[data-problem-scale-dynamic-text]',
		previousValue: '[data-problem-scale-previous-value]',
		currentValue: '[data-problem-scale-current-value]',
		previousYear: '[data-problem-scale-previous-year]',
		currentYear: '[data-problem-scale-current-year]',
		previousBar: '[data-problem-scale-previous-bar]',
		currentBar: '[data-problem-scale-current-bar]',
		badgePercent: '[data-problem-scale-badge-percent]',
		badgeText: '[data-problem-scale-badge-text]',
	};

	static classes = {
		visible: 'problem-scale-card--visible',
	};

	constructor(element) {
		this.element = element;
		this.config = this.getConfig();

		this.summary = this.config.summary || {};
		this.defaultYear = String(this.config.defaultYear || '');
		this.compareYear = String(this.config.compareYear || '');

		this.elements = {
			dynamicText: element.querySelector(
				ProblemScaleCard.selectors.dynamicText
			),
			previousValue: element.querySelector(
				ProblemScaleCard.selectors.previousValue
			),
			currentValue: element.querySelector(
				ProblemScaleCard.selectors.currentValue
			),
			previousYear: element.querySelector(
				ProblemScaleCard.selectors.previousYear
			),
			currentYear: element.querySelector(
				ProblemScaleCard.selectors.currentYear
			),
			previousBar: element.querySelector(
				ProblemScaleCard.selectors.previousBar
			),
			currentBar: element.querySelector(
				ProblemScaleCard.selectors.currentBar
			),
			badgePercent: element.querySelector(
				ProblemScaleCard.selectors.badgePercent
			),
			badgeText: element.querySelector(
				ProblemScaleCard.selectors.badgeText
			),
		};

		this.render(this.defaultYear);
	}

	getConfig() {
		try {
			return JSON.parse(
				this.element.dataset.problemScale || '{}'
			);
		} catch (error) {
			return {};
		}
	}

	render(year) {
		const currentYear = String(
			year || this.defaultYear
		);

		const currentData = this.getYearData(currentYear);

		const comparison = this.getComparison(
			currentYear,
			currentData,
			this.compareYear
		);

		this.renderDynamicText(comparison);
		this.renderChart(comparison);
		this.renderBadge(comparison);

		requestAnimationFrame(() => {
			this.element.classList.add(
				ProblemScaleCard.classes.visible
			);
		});
	}

	getYearData(year) {
		const data = this.summary[String(year)];

		if (!data) {
			return {
				totalReports: 0,
			};
		}

		return data;
	}

	getComparison(currentYear, currentData, compareYear) {
		let previousYear = String(compareYear || '');

		if (!previousYear) {
			previousYear = String(
				Number(currentYear) - 1
			);
		}

		const previousData = this.getYearData(previousYear);

		const currentTotal = Number(
			currentData.totalReports || 0
		);

		const previousTotal = Number(
			previousData.totalReports || 0
		);

		const difference = currentTotal - previousTotal;

		const percentage = previousTotal > 0
			? Math.ceil(
				(difference / previousTotal) * 100
			)
			: 0;

		return {
			currentYear,
			previousYear,
			currentTotal,
			previousTotal,
			difference,
			percentage: Math.abs(percentage),
			isIncrease: difference >= 0,
		};
	}

	renderDynamicText(comparison) {
		if (!this.elements.dynamicText) {
			return;
		}

		this.elements.dynamicText.textContent =
			this.replaceTemplate(
				this.config.dynamicText || '',
				{
					year: comparison.currentYear,
					previous_year: comparison.previousYear,
					percent: comparison.percentage,
				}
			);
	}

	renderChart(comparison) {
		const maxValue = Math.max(
			comparison.currentTotal,
			comparison.previousTotal,
			1
		);

		const previousHeight =
			(comparison.previousTotal / maxValue) * 100;

		const currentHeight =
			(comparison.currentTotal / maxValue) * 100;

		if (this.elements.previousValue) {
			this.animateNumber(
				this.elements.previousValue,
				comparison.previousTotal
			);
		}

		if (this.elements.currentValue) {
			this.animateNumber(
				this.elements.currentValue,
				comparison.currentTotal
			);
		}

		if (this.elements.previousYear) {
			this.elements.previousYear.textContent =
				comparison.previousYear;
		}

		if (this.elements.currentYear) {
			this.elements.currentYear.textContent =
				comparison.currentYear;
		}

		if (this.elements.previousBar) {
			this.elements.previousBar.style.setProperty(
				'--bar-height',
				`${previousHeight}%`
			);
		}

		if (this.elements.currentBar) {
			this.elements.currentBar.style.setProperty(
				'--bar-height',
				`${currentHeight}%`
			);
		}
	}

	renderBadge(comparison) {
		if (this.elements.badgePercent) {
			const icon = comparison.isIncrease
				? '↑'
				: '↓';

			this.elements.badgePercent.textContent =
				`${icon} ${comparison.percentage}%`;
		}

		if (this.elements.badgeText) {
			this.elements.badgeText.textContent =
				this.replaceTemplate(
					this.config.bottomText || '',
					{
						year: comparison.currentYear,
						previous_year: comparison.previousYear,
						percent: comparison.percentage,
					}
				);
		}
	}

	replaceTemplate(template, values) {
		return template.replace(
			/\{([a-z_]+)\}/g,
			(match, key) => {
				if (values[key] === undefined) {
					return match;
				}

				return String(values[key]);
			}
		);
	}

	animateNumber(element, target) {
		const duration = 900;
		const startTime = performance.now();

		const update = (currentTime) => {
			const progress = Math.min(
				(currentTime - startTime) / duration,
				1
			);

			const value = Math.round(
				target * progress
			);

			element.textContent = String(value);

			if (progress < 1) {
				requestAnimationFrame(update);
			}
		};

		requestAnimationFrame(update);
	}
}

document.addEventListener('DOMContentLoaded', () => {
	document
		.querySelectorAll(
			ProblemScaleCard.selectors.block
		)
		.forEach((element) => {
			new ProblemScaleCard(element);
		});
});